import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, AlertCircle, CalendarClock, Hourglass, MessageSquareReply, Phone, StickyNote, ArrowRightLeft, Mail, CheckCircle2, Sparkles, UserPlus, FileText, Siren, IndianRupee, Users } from "lucide-react";
import { api, fromNow } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { Button } from "@/components/ui/button";
import { StageAge, Empty, Skeletons } from "@/components/crm/bits";

export const ACT_ICON = { call: Phone, meeting: Users, note: StickyNote, whatsapp: MessageSquareReply, visit: UserPlus, stage_change: ArrowRightLeft,
  email: Mail, task_done: CheckCircle2, task: CalendarClock, ai: Sparkles, created: Plus, assignment: UserPlus, quotation: FileText,
  requirements: FileText, document: FileText, escalation: Siren };

const Metric = ({ label, value, sub, tone, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="card-surface p-5 text-left hover:border-[#A3D9C9] transition-colors">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p className={`font-display text-3xl font-bold mt-2 tracking-tight ${tone || "text-gray-900"}`}>{value ?? "—"}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </button>
);

function PriorityGroup({ icon: Icon, title, items, tone, render }) {
  if (!items?.length) return null;
  return (
    <div className="py-3 first:pt-0">
      <div className={`flex items-center gap-2 text-xs font-semibold mb-2 ${tone || "text-gray-600"}`}><Icon className="h-3.5 w-3.5" />{title}<span className="text-gray-400 font-normal">{items.length}</span></div>
      <div className="space-y-1">{items.map(render)}</div>
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const { setAddLeadOpen, version } = useMeta();
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, [version]);

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const LeadRow = (l) => (
    <button key={l.lead_id + (l.task_id || "")} onClick={() => nav(`/leads/${l.lead_id}`)} data-testid={`priority-item-${l.lead_id}`}
      className="w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-gray-50 text-left">
      <span className="min-w-0"><span className="block text-sm font-medium truncate">{l.company}</span>
        <span className="block text-xs text-gray-500 truncate">{l.stage_name} · {l.owner_name}{l.next_action ? ` · ${l.next_action}` : ""}</span></span>
      <StageAge lead={l} />
    </button>
  );
  const TaskRow = (t) => (
    <button key={t.task_id} onClick={() => (t.lead_id ? nav(`/leads/${t.lead_id}`) : nav("/tasks"))} data-testid={`priority-task-${t.task_id}`}
      className="w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-gray-50 text-left">
      <span className="min-w-0"><span className="block text-sm font-medium truncate">{t.title}</span>
        <span className="block text-xs text-gray-500 truncate">{t.company || "No lead"} · {t.assignee_name}</span></span>
      <span className="text-[11px] font-medium text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 whitespace-nowrap">Due today</span>
    </button>
  );
  const p = d?.priorities;
  const nothing = p && !["tasks_today", "followups_today", "waiting", "approaching", "overdue", "missing_eyt"].some((k) => p[k]?.length);
  const maxCount = Math.max(1, ...(d?.pipeline || []).map((x) => x.count));

  return (
    <div className="space-y-6 fade-in" data-testid="overview-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs text-gray-500">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-1" data-testid="welcome-heading">{greet}, {user?.name?.split(" ")[0]}</h1>
        </div>
        <Button onClick={() => setAddLeadOpen(true)} className="bg-[#0B3C2D] hover:bg-[#072B20] h-10" data-testid="dashboard-add-lead-button"><Plus className="h-4 w-4 mr-1.5" />Add Lead</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Metric testid="metric-active-leads" label="Total active leads" value={d?.metrics.total_active} onClick={() => nav("/leads")} />
        <Metric testid="metric-new-leads" label="New leads this month" value={d?.metrics.new_this_month} onClick={() => nav("/leads?sort=created_at")} />
        <Metric testid="metric-followups-today" label="Follow-ups due today" value={d?.metrics.followups_today} tone={d?.metrics.followups_today ? "text-amber-700" : ""} onClick={() => nav("/tasks")} />
        <Metric testid="metric-overdue-leads" label="Overdue leads" value={d?.metrics.overdue} sub={`No action in ${d?.limit_days || 5}+ days`} tone={d?.metrics.overdue ? "text-red-600" : ""} onClick={() => nav("/leads?age_status=overdue")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
        <section className="card-surface p-5 min-w-0" data-testid="my-priorities">
          <h2 className="font-display text-base font-semibold mb-4">My priorities</h2>
          {!d ? <Skeletons /> : nothing ? <Empty icon={CheckCircle2} title="Nothing urgent" text="All leads are moving within the five-day window." /> : (
            <div className="divide-y divide-gray-100">
              <PriorityGroup icon={AlertCircle} title="Overdue — action required" tone="text-red-600" items={p.overdue} render={LeadRow} />
              <PriorityGroup icon={CalendarClock} title="Follow-ups due today" tone="text-amber-700" items={p.tasks_today} render={TaskRow} />
              <PriorityGroup icon={CalendarClock} title="Leads with follow-up today" items={p.followups_today.filter((l) => !p.tasks_today.some((t) => t.lead_id === l.lead_id))} render={LeadRow} />
              <PriorityGroup icon={Hourglass} title="Approaching the five-day limit" tone="text-amber-700" items={p.approaching} render={LeadRow} />
              <PriorityGroup icon={MessageSquareReply} title="Waiting for customer response" items={p.waiting} render={LeadRow} />
              <PriorityGroup icon={IndianRupee} title="EYT missing — capture turnover" tone="text-amber-700" items={p.missing_eyt} render={LeadRow} />
            </div>
          )}
        </section>
        <section className="card-surface p-5" data-testid="pipeline-summary">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-base font-semibold">Lead pipeline</h2>
            <button className="text-xs text-[#0B3C2D] hover:underline" onClick={() => nav("/pipeline")} data-testid="open-pipeline-link">Open board</button>
          </div>
          <div className="space-y-3.5">
            {(d?.pipeline || []).map((g) => (
              <div key={g.name} data-testid={`pipeline-group-${g.name.toLowerCase()}`}>
                <div className="flex justify-between text-sm"><span className="text-gray-700">{g.name}</span><span className="font-mono-num font-medium">{g.count}</span></div>
                <div className="h-1.5 rounded-full bg-gray-100 mt-1.5 overflow-hidden">
                  <div className={`h-full rounded-full ${g.name === "Won" ? "bg-emerald-500" : "bg-[#0B3C2D]"}`} style={{ width: `${(g.count / maxCount) * 100}%`, transition: "width .5s ease" }} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="card-surface p-5" data-testid="recent-activity">
        <h2 className="font-display text-base font-semibold mb-4">Recent activity</h2>
        {!d ? <Skeletons n={3} /> : d.recent_activity.length === 0 ? <Empty title="No activity yet" /> : (
          <div className="divide-y divide-gray-50">
            {d.recent_activity.map((a) => {
              const I = ACT_ICON[a.type] || StickyNote;
              return (
                <button key={a.activity_id} onClick={() => nav(`/leads/${a.lead_id}`)} className="w-full flex gap-3 py-2.5 text-left hover:bg-gray-50/60 rounded-md px-1">
                  <span className="h-7 w-7 rounded-full bg-gray-50 ring-1 ring-gray-100 grid place-items-center shrink-0"><I className="h-3.5 w-3.5 text-gray-500" /></span>
                  <span className="min-w-0 flex-1"><span className="block text-sm"><span className="font-medium">{a.company}</span> <span className="text-gray-600">— {a.content.split("\n")[0]}</span></span>
                    <span className="block text-xs text-gray-400 mt-0.5">{a.user_name} · {fromNow(a.created_at)}</span></span>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
